import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Shield, Building, User, Mail, Lock, Loader2, CheckCircle2, AlertTriangle, ArrowRight, ArrowLeft } from 'lucide-react';

const industries = [
  'Technology & Software', 'Financial Services', 'Healthcare & Life Sciences',
  'Manufacturing', 'Professional Services', 'Retail & E-commerce',
  'Government & Public Sector', 'Education', 'Energy & Utilities', 'Other',
];

const companySizes = ['1-10', '11-50', '51-200', '201-500', '501-1000', '1000+'];

type Step = 1 | 2;

interface FormData {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  confirmPassword: string;
  companyName: string;
  industry: string;
  companySize: string;
}

export default function Register() {
  const navigate = useNavigate();
  const [step, setStep] = useState<Step>(1);
  const [loading, setLoading] = useState(false);
  const [checkingEnabled, setCheckingEnabled] = useState(true);
  const [signupEnabled, setSignupEnabled] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const [form, setForm] = useState<FormData>({
    firstName: '', lastName: '', email: '', password: '', confirmPassword: '',
    companyName: '', industry: '', companySize: '',
  });

  useEffect(() => {
    // Check if self-signup is enabled
    fetch('/api/settings/public/self_signup_enabled')
      .then(res => res.ok ? res.json() : { data: { configValue: 'false' } })
      .then(json => setSignupEnabled(json.data?.configValue === 'true' || json.data?.config_value === 'true'))
      .catch(() => setSignupEnabled(false))
      .finally(() => setCheckingEnabled(false));
  }, []);

  const set = (key: keyof FormData) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
    setForm(prev => ({ ...prev, [key]: e.target.value }));

  const canNext = step === 1
    ? !!(form.firstName && form.lastName && form.email && form.password && form.confirmPassword)
    : !!(form.companyName);

  const passwordValid = form.password.length >= 8;
  const passwordsMatch = form.password === form.confirmPassword;

  const handleSubmit = async () => {
    if (!passwordValid) { setError('Password must be at least 8 characters'); return; }
    if (!passwordsMatch) { setError('Passwords do not match'); return; }

    setLoading(true);
    setError('');
    try {
      const res = await fetch('/api/auth/register', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          firstName: form.firstName,
          lastName: form.lastName,
          email: form.email,
          password: form.password,
          companyName: form.companyName,
          industry: form.industry || undefined,
          companySize: form.companySize || undefined,
        }),
      });

      const data = await res.json();

      if (!res.ok) {
        setError(data.message || 'Registration failed');
        return;
      }

      // Store tokens for auto-login
      if (data.data?.accessToken) {
        localStorage.setItem('token', data.data.accessToken);
        localStorage.setItem('refreshToken', data.data.refreshToken);
      }

      setSuccess(true);
      // Redirect to dashboard after short delay
      setTimeout(() => navigate('/company/dashboard'), 2000);
    } catch {
      setError('Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Loading state
  if (checkingEnabled) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <Loader2 className="text-blue-400 animate-spin" size={32} />
      </div>
    );
  }

  // Self-signup disabled
  if (!signupEnabled) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-slate-800 rounded-2xl border border-slate-700 p-8 text-center">
          <Shield className="text-blue-400 mx-auto mb-4" size={48} />
          <h1 className="text-2xl font-bold text-white mb-3">Registration Unavailable</h1>
          <p className="text-slate-400 mb-6">
            Self-registration is currently disabled. Please contact your system administrator to get an account set up.
          </p>
          <div className="flex flex-col gap-3">
            <a
              href="mailto:support@eis-tech.co.uk"
              className="inline-flex items-center justify-center gap-2 px-5 py-3 bg-blue-600 text-white font-medium rounded-xl hover:bg-blue-700 transition-colors"
            >
              <Mail size={16} /> Contact Support
            </a>
            <Link
              to="/login"
              className="text-sm text-blue-400 hover:text-blue-300 transition-colors"
            >
              Already have an account? Sign in
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Success state
  if (success) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-slate-800 rounded-2xl border border-slate-700 p-8 text-center">
          <div className="w-16 h-16 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle2 className="text-emerald-400" size={32} />
          </div>
          <h1 className="text-2xl font-bold text-white mb-2">Welcome to EIS-Tech!</h1>
          <p className="text-slate-400 mb-4">
            Your account and company have been created. Redirecting to your dashboard...
          </p>
          <Loader2 className="text-blue-400 animate-spin mx-auto" size={24} />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4">
      <div className="max-w-lg w-full">
        {/* Logo */}
        <div className="text-center mb-8">
          <Shield className="text-blue-400 mx-auto mb-3" size={40} />
          <h1 className="text-3xl font-bold text-white">Create Your Account</h1>
          <p className="text-slate-400 mt-1">Set up your company compliance platform</p>
        </div>

        {/* Steps indicator */}
        <div className="flex items-center justify-center gap-3 mb-6">
          <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-bold ${step === 1 ? 'bg-blue-600 text-white' : 'bg-emerald-500/20 text-emerald-400'}`}>
            {step > 1 ? <CheckCircle2 size={14} /> : <User size={14} />}
            Your Details
          </div>
          <div className="w-6 h-0.5 bg-slate-700" />
          <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-bold ${step === 2 ? 'bg-blue-600 text-white' : 'bg-slate-700 text-slate-400'}`}>
            <Building size={14} />
            Company
          </div>
        </div>

        {/* Form card */}
        <div className="bg-slate-800 rounded-2xl border border-slate-700 p-6">
          {error && (
            <div className="flex items-center gap-2 bg-red-500/10 border border-red-500/30 text-red-400 text-sm rounded-lg px-4 py-3 mb-4">
              <AlertTriangle size={16} />
              {error}
            </div>
          )}

          {step === 1 && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-slate-400 mb-1.5 uppercase tracking-wider">First Name *</label>
                  <input type="text" value={form.firstName} onChange={set('firstName')} className="w-full px-4 py-3 bg-slate-900 border border-slate-700 rounded-xl text-white text-sm focus:outline-none focus:border-blue-500" placeholder="John" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-400 mb-1.5 uppercase tracking-wider">Last Name *</label>
                  <input type="text" value={form.lastName} onChange={set('lastName')} className="w-full px-4 py-3 bg-slate-900 border border-slate-700 rounded-xl text-white text-sm focus:outline-none focus:border-blue-500" placeholder="Smith" />
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-400 mb-1.5 uppercase tracking-wider">Work Email *</label>
                <input type="email" value={form.email} onChange={set('email')} className="w-full px-4 py-3 bg-slate-900 border border-slate-700 rounded-xl text-white text-sm focus:outline-none focus:border-blue-500" placeholder="john@company.com" />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-400 mb-1.5 uppercase tracking-wider">Password *</label>
                <input type="password" value={form.password} onChange={set('password')} className="w-full px-4 py-3 bg-slate-900 border border-slate-700 rounded-xl text-white text-sm focus:outline-none focus:border-blue-500" placeholder="Min. 8 characters" />
                {form.password && !passwordValid && <p className="text-red-400 text-xs mt-1">Must be at least 8 characters</p>}
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-400 mb-1.5 uppercase tracking-wider">Confirm Password *</label>
                <input type="password" value={form.confirmPassword} onChange={set('confirmPassword')} className="w-full px-4 py-3 bg-slate-900 border border-slate-700 rounded-xl text-white text-sm focus:outline-none focus:border-blue-500" placeholder="Repeat password" />
                {form.confirmPassword && !passwordsMatch && <p className="text-red-400 text-xs mt-1">Passwords do not match</p>}
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-medium text-slate-400 mb-1.5 uppercase tracking-wider">Company Name *</label>
                <input type="text" value={form.companyName} onChange={set('companyName')} className="w-full px-4 py-3 bg-slate-900 border border-slate-700 rounded-xl text-white text-sm focus:outline-none focus:border-blue-500" placeholder="Acme Ltd" />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-400 mb-1.5 uppercase tracking-wider">Industry</label>
                <select value={form.industry} onChange={set('industry')} className="w-full px-4 py-3 bg-slate-900 border border-slate-700 rounded-xl text-white text-sm focus:outline-none focus:border-blue-500">
                  <option value="">Select industry...</option>
                  {industries.map(i => <option key={i} value={i}>{i}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-400 mb-1.5 uppercase tracking-wider">Company Size</label>
                <select value={form.companySize} onChange={set('companySize')} className="w-full px-4 py-3 bg-slate-900 border border-slate-700 rounded-xl text-white text-sm focus:outline-none focus:border-blue-500">
                  <option value="">Select size...</option>
                  {companySizes.map(s => <option key={s} value={s}>{s} employees</option>)}
                </select>
              </div>
            </div>
          )}

          {/* Navigation */}
          <div className="flex items-center justify-between mt-6 pt-4 border-t border-slate-700">
            {step === 2 ? (
              <button onClick={() => setStep(1)} className="inline-flex items-center gap-1.5 text-sm text-slate-400 hover:text-white transition-colors">
                <ArrowLeft size={16} /> Back
              </button>
            ) : (
              <div />
            )}

            {step === 1 ? (
              <button
                onClick={() => { setError(''); setStep(2); }}
                disabled={!canNext || !passwordValid || !passwordsMatch}
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-blue-600 text-white font-medium text-sm rounded-xl hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Continue <ArrowRight size={16} />
              </button>
            ) : (
              <button
                onClick={handleSubmit}
                disabled={loading || !canNext}
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-emerald-600 text-white font-medium text-sm rounded-xl hover:bg-emerald-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? <><Loader2 size={16} className="animate-spin" /> Creating...</> : <><CheckCircle2 size={16} /> Create Account</>}
              </button>
            )}
          </div>
        </div>

        <p className="text-center text-sm text-slate-500 mt-4">
          Already have an account? <Link to="/login" className="text-blue-400 hover:text-blue-300">Sign in</Link>
        </p>
      </div>
    </div>
  );
}
